import React from 'react';
import styled from 'styled-components';
import { Tag } from '@decisiv/ui-components';
import SelectMenu from '@decisiv/ui-components/lib/components/SelectMenu';
import AdminPage from 'lib/admin-kit/components/AdminPage';
import AdminTable from 'lib/admin-kit/components/AdminTable';

const DEALER_BUDGETS_ENDPOINT = '/internal_admin/admin_kit/ai_dealer_budgets';

const TIER_INTENT = { warn: 'warning', exceeded: 'danger' };
const TIER_LABEL = { warn: 'Warn', exceeded: 'Exceeded' };

const formatUsd = (value) =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`;

const PERIODS = [
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'Last 30 days' },
  { id: 'all', label: 'All' },
];

// SelectMenu renders its `label` prop as a plain <span> beside the trigger
// button, making the label text a non-interactive dead zone. Same fix as
// AdminFilters: pass label=" " and hide that span via CSS.
const Wrapper = styled.div`
  margin-bottom: 16px;

  & > div > span:first-child {
    display: none;
  }
`;

// Full-page navigation with a `?period=` query param — the controller
// re-renders fresh props, so no client-side fetch/state is needed here.
// Only week/month/all — this view has no "day" granularity.
const ExceededHistoryPeriodSelector = ({ period, basePath }) => (
  <Wrapper>
    <SelectMenu
      label=" "
      options={PERIODS}
      selected={period}
      size="small"
      onChange={(id) => {
        const params = new URLSearchParams({ period: id });
        window.location.href = `${basePath}?${params.toString()}`;
      }}
    />
  </Wrapper>
);

const exceededHistoryColumns = [
  {
    name: 'day',
    title: 'Date',
    DataCell: ({ rowData }) => {
      if (!rowData.day) return '—';
      const date = new Date(rowData.day);
      return date.toLocaleDateString();
    },
  },
  {
    name: 'tier',
    title: 'Tier',
    DataCell: ({ rowData }) => (
      <Tag
        intent={TIER_INTENT[rowData.tier] ?? 'neutral'}
        text={TIER_LABEL[rowData.tier] ?? rowData.tier}
      />
    ),
  },
  {
    name: 'triggered_at',
    title: 'Triggered At',
    DataCell: ({ rowData }) => {
      if (!rowData.triggered_at) return '—';
      const date = new Date(rowData.triggered_at);
      return date.toLocaleTimeString();
    },
  },
  {
    name: 'threshold_usd',
    title: 'Threshold at Trigger',
    DataCell: ({ rowData }) => formatUsd(rowData.threshold_usd),
  },
];

const ExceededHistory = ({
  dealerId,
  dealerName,
  rows = [],
  period = 'month',
}) => {
  const basePath = `${DEALER_BUDGETS_ENDPOINT}/exceeded_history/${dealerId}`;

  return (
    <AdminPage
      title={`Budget History — ${dealerName ?? dealerId}`}
      breadcrumbs={[
        { text: 'AI Dealer Budgets', href: DEALER_BUDGETS_ENDPOINT },
        { text: dealerName ?? dealerId },
      ]}
    >
      <ExceededHistoryPeriodSelector period={period} basePath={basePath} />

      <div>
        <AdminTable
          columns={exceededHistoryColumns}
          data={rows}
          getRowKey={(r) => `${r.day}-${r.tier}-${r.threshold_usd}`}
          emptyText="No warn/exceeded events recorded for this dealer in this period."
        />
      </div>
    </AdminPage>
  );
};

export default ExceededHistory;
